import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import {
    HandlingPartySelect,
    type HandlingPartySelectQueryState,
} from '@client/components/_shared/selectors/common/HandlingPartySelect';
import { InsurerSelect } from '@client/components/_shared/selectors/common/InsurerSelect';
import { ProductTypeSelect } from '@client/components/_shared/selectors/products/ProductTypeSelect';
import { SupplierSelect } from '@client/components/_shared/selectors/suppliers/SupplierSelect';
import { RangePicker } from '@client/components/_ui/data-entry/RangePicker';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { ProcessProgressCard } from '@client/components/_ui/feedback/ProcessProgressCard';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useIsTrainingManagerOnly } from '@client/hooks/auth/useIsTrainingManagerOnly';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePendingProgress } from '@client/hooks/ui/usePendingProgress';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { capture } from '@client/utils/analytics';
import { REPORT_TYPE_MODULE_MAP, REPORT_TYPES, type ReportType } from '@shared/constants/business/report';
import { ORG_ROLES } from '@shared/constants/business/user';
import { hasPermission } from '@shared/permissions/roles';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Col, Form, Row, Select, Spin, Switch } from 'antd'; // curium:allow-spin — inline leaf spinner beside the "Loading handling parties..." text.
import { CircleAlert, FileSpreadsheet } from 'lucide-react';
import { useId, useState } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute('/console/$orgId/reporting/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Generate a new report',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReportAdd />
        </PageController>
    );
}

type Report = RouterInputs['report']['generateReport'];

const FormItem = Form.Item<Report>;

/** Reports build inside the request; most finish well within this, large orgs can run over. */
const REPORT_EXPECTED_SECONDS = 60;

/**
 * Noun spliced into the handling-parties field copy ("Report on complaints handled by").
 * Staff training types are intentionally absent — parties filter staff there, not records
 * "handled by" them — so they fall back to the static label.
 */
const REPORT_TYPE_HANDLED_NOUN: Partial<Record<ReportType, string>> = {
    [REPORT_TYPES.CLAIMS]: 'claims',
    [REPORT_TYPES.CLAIMS_BORDEREAU]: 'claims',
    [REPORT_TYPES.CLAIMS_GICOP_BREACH]: 'breaches',
    [REPORT_TYPES.COMPLAINTS]: 'complaints',
    [REPORT_TYPES.COMPLAINTS_ASIC_2023]: 'complaints',
    [REPORT_TYPES.INCIDENTS]: 'incidents',
    [REPORT_TYPES.IBCCC_BREACH_2025]: 'breaches',
};

function generateLastTenPeriodsForAsicReport() {
    const periods: string[] = [];
    const currentDate = new Date();

    let currentYear = currentDate.getFullYear();
    let currentPeriod = currentDate.getMonth() < 6 ? 1 : 2;

    for (let i = 0; i < 10; i++) {
        periods.unshift(`${currentPeriod}/${currentYear}`);

        if (currentPeriod === 1) {
            currentPeriod = 2;
            currentYear--;
        } else {
            currentPeriod = 1;
        }
    }

    return periods;
}

function useReportTypeOptions() {
    const org = useOrg();
    const isBordereauReportEnabled = useFeatureFlag('is-bordereau-report-enabled');
    const isTrainingManagerOnly = useIsTrainingManagerOnly();

    const options: {
        module: (typeof org.enabledModules)[number];
        label: string;
        /** When set, this group is only shown for training manager-only users */
        trainingOnly?: boolean;
        options: {
            label: string;
            value: string;
        }[];
    }[] = [
        {
            module: 'claim',
            label: 'Claims',
            options: [
                {
                    label: 'Claims',
                    value: REPORT_TYPES.CLAIMS,
                },
                ...(isBordereauReportEnabled
                    ? [
                          {
                              label: 'Claims - Bordereau',
                              value: REPORT_TYPES.CLAIMS_BORDEREAU,
                          },
                      ]
                    : []),
                {
                    label: 'Claims - GICOP breaches',
                    value: REPORT_TYPES.CLAIMS_GICOP_BREACH,
                },
            ],
        },
        {
            module: 'compliance',
            label: 'Complaints',
            options: [
                {
                    label: 'Complaints',
                    value: REPORT_TYPES.COMPLAINTS,
                },
                {
                    label: 'Complaints - ASIC 2023',
                    value: REPORT_TYPES.COMPLAINTS_ASIC_2023,
                },
            ],
        },
        {
            module: 'compliance',
            label: 'Incidents',
            options: [
                {
                    label: 'Incidents',
                    value: REPORT_TYPES.INCIDENTS,
                },
                {
                    label: 'IBCCC 2025 breach data report',
                    value: REPORT_TYPES.IBCCC_BREACH_2025,
                },
            ],
        },
        {
            module: 'compliance',
            label: 'Training',
            trainingOnly: true,
            options: [
                {
                    label: 'Staff training summary report',
                    value: REPORT_TYPES.STAFF_TRAINING_SUMMARY,
                },
                {
                    label: 'Staff training detailed report',
                    value: REPORT_TYPES.STAFF_TRAINING_DETAILED,
                },
            ],
        },
        {
            module: 'risk',
            label: 'Risk',
            options: [
                {
                    label: 'Risk',
                    value: REPORT_TYPES.RISK,
                },
            ],
        },
    ];

    return options
        .filter((item) => org.enabledModules.includes(item.module))
        .filter((item) => !isTrainingManagerOnly || item.trainingOnly)
        .flatMap((item) => ({
            label: item.label,
            options: item.options,
        }));
}

function ReportAdd() {
    const orgId = useOrgId();
    const formId = useId();
    const reportTypeOptions = useReportTypeOptions();
    const isTrainingManagerOnly = useIsTrainingManagerOnly();
    const { trpcUtils } = useTrpc();
    const { fDateShortDayJs } = useFormatter();
    const { mutate, isPending } = trpc.report.generateReport.useMutation();
    // The report is built and stored inside the request, so the drawer shows elapsed progress
    // rather than a toast while it waits.
    const generation = usePendingProgress({ isPending, expectedDurationSeconds: REPORT_EXPECTED_SECONDS });
    const navigate = useNavigate();

    const user = useUser();
    const [form] = Form.useForm<Report>();
    const formDirty = useFormDirty();
    const type = Form.useWatch(({ type }) => type, form);
    const handlingPartyIds = Form.useWatch(
        (values) => ('handlingPartyIds' in values ? values.handlingPartyIds : undefined),
        form,
    );
    const handledNoun = type ? REPORT_TYPE_HANDLED_NOUN[type] : undefined;
    const reportLabel = reportTypeOptions
        .flatMap((group) => group.options)
        .find((option) => option.value === type)?.label;

    const scopeShape = (reportType: Report['type'] | undefined) => {
        if (!reportType || reportType === 'RISK') return 'none';
        if (
            !hasPermission(user.roles, 'org:admin') &&
            !user.roles.includes(ORG_ROLES.TRAINING_MANAGER) &&
            (reportType === 'STAFF_TRAINING_SUMMARY' || reportType === 'STAFF_TRAINING_DETAILED')
        ) {
            return 'hidden';
        }
        return 'visible';
    };
    const isRegularUserTrainingReport = scopeShape(type) === 'hidden';

    // Query status of the hidden auto-selected handling-party field (regular-user training
    // reports). Owns the visible error/retry affordance and gates report generation (CUR-796).
    const [hiddenPartiesQuery, setHiddenPartiesQuery] = useState<HandlingPartySelectQueryState | null>(null);
    const [visiblePartiesQuery, setVisiblePartiesQuery] = useState<HandlingPartySelectQueryState | null>(null);
    const hasVisibleScope = scopeShape(type) === 'visible';
    const visibleScopeUnavailable = visiblePartiesQuery?.isReady && !visiblePartiesQuery.hasSelectableOptions;
    const isUnassignedRegularUser =
        !hasPermission(user.roles, 'org:admin') &&
        !user.handlingPartyIds.length &&
        !user.roles.includes(ORG_ROLES.TRAINING_MANAGER);
    const scopeBlocksGeneration =
        (hasVisibleScope && (!visiblePartiesQuery?.isReady || !visiblePartiesQuery.hasSelectableOptions)) ||
        (isRegularUserTrainingReport && (!hiddenPartiesQuery?.isReady || !handlingPartyIds?.length));

    const claimsReportFiltersQuery = trpc.claim.getClaimsColumnsForFiltering.useQuery(
        type === 'CLAIMS' || type === 'CLAIMS_BORDEREAU' || type === 'CLAIMS_GICOP_BREACH' ? undefined : skipToken,
    );

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            title="Generate a new report"
            size="middle"
            afterClose={() => {
                form.resetFields();
                void trpcUtils.report.invalidate();
                navigate({
                    to: '/console/$orgId/reporting',
                    params: {
                        orgId,
                    },
                });
            }}
            footer={({ requestClose }) => (
                <DrawerFooter
                    onCancel={requestClose}
                    formId={formId}
                    okText="Generate report"
                    okLoading={isPending}
                    okDisabled={scopeBlocksGeneration}
                />
            )}
        >
            {({ onClose }) => (
                <>
                    {isPending && (
                        <ProcessProgressCard
                            icon={<FileSpreadsheet className="h-5 w-5" />}
                            title="Generating report"
                            statusMessage={
                                reportLabel ? `Building the ${reportLabel} report...` : 'Building the report...'
                            }
                            progress={generation.progress}
                            elapsedTime={generation.formattedElapsedTime}
                            expectedDurationLabel="under a minute"
                            isOvertime={generation.isOvertime}
                        />
                    )}
                    {/* The form stays mounted (hidden) so its values survive a failed attempt. */}
                    <Form
                        id={formId}
                        layout="vertical"
                        form={form}
                        className={isPending ? 'hidden' : undefined}
                        {...formDirty.formProps}
                        onFinish={(values) => {
                            if (scopeBlocksGeneration) return;

                            mutate(
                                match(values)
                                    .with({ type: 'RISK' }, (value) => ({
                                        type: value.type,
                                    }))
                                    .with({ type: 'COMPLAINTS_ASIC_2023' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        asicReportingPeriod: value.asicReportingPeriod,
                                    }))
                                    .with({ type: 'COMPLAINTS' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        dateRange: value.dateRange,
                                    }))
                                    .with({ type: 'INCIDENTS' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        dateRange: value.dateRange,
                                    }))
                                    .with({ type: 'IBCCC_BREACH_2025' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        dateRange: value.dateRange,
                                    }))
                                    .with({ type: 'STAFF_TRAINING_SUMMARY' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        dateRange: value.dateRange,
                                        includeDisabledUsers: value.includeDisabledUsers,
                                    }))
                                    .with({ type: 'STAFF_TRAINING_DETAILED' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        dateRange: value.dateRange,
                                        includeDisabledUsers: value.includeDisabledUsers,
                                    }))
                                    .with({ type: 'CLAIMS' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        dateRange: value.dateRange,
                                        productTypeIds: value.productTypeIds,
                                        insurerIds: value.insurerIds,
                                        brokerCompanies: value.brokerCompanies,
                                        supplierIds: value.supplierIds,
                                        statuses: value.statuses,
                                        decisions: value.decisions,
                                        triageOutcomes: value.triageOutcomes,
                                        causesOfLoss: value.causesOfLoss,
                                    }))
                                    .with({ type: 'CLAIMS_BORDEREAU' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        dateRange: value.dateRange,
                                        productTypeIds: value.productTypeIds,
                                        insurerIds: value.insurerIds,
                                        brokerCompanies: value.brokerCompanies,
                                        supplierIds: value.supplierIds,
                                        statuses: value.statuses,
                                        decisions: value.decisions,
                                        triageOutcomes: value.triageOutcomes,
                                        causesOfLoss: value.causesOfLoss,
                                    }))
                                    .with({ type: 'CLAIMS_GICOP_BREACH' }, (value) => ({
                                        type: value.type,
                                        handlingPartyIds: value.handlingPartyIds,
                                        dateRange: value.dateRange,
                                        productTypeIds: value.productTypeIds,
                                        insurerIds: value.insurerIds,
                                        brokerCompanies: value.brokerCompanies,
                                        supplierIds: value.supplierIds,
                                        statuses: value.statuses,
                                        decisions: value.decisions,
                                        triageOutcomes: value.triageOutcomes,
                                        causesOfLoss: value.causesOfLoss,
                                    }))
                                    .exhaustive(),
                                {
                                    onSuccess() {
                                        message.success('Report generated');
                                        capture('report_generated', {
                                            report_type: type,
                                            module: REPORT_TYPE_MODULE_MAP[type],
                                        });
                                        onClose();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        }}
                    >
                        <FormItem
                            label="Report"
                            name="type"
                            rules={[{ required: true, message: 'Choose a report' }]}
                            extra={
                                type === 'STAFF_TRAINING_SUMMARY'
                                    ? 'Only users with training points tracking switched on appear in the staff training summary report. If tracking is switched off for your account, the generated report will contain headers only.'
                                    : undefined
                            }
                        >
                            <Select<Report['type']>
                                options={reportTypeOptions}
                                onChange={(value) => {
                                    setHiddenPartiesQuery(null);
                                    setVisiblePartiesQuery(null);
                                    if (scopeShape(type) !== scopeShape(value)) {
                                        form.setFieldValue('handlingPartyIds', undefined);
                                    }
                                    if (value === 'IBCCC_BREACH_2025') {
                                        form.setFieldsValue({
                                            dateRange: [
                                                new Date('2025-01-01T00:00:00'),
                                                new Date('2025-12-31T00:00:00'),
                                            ],
                                        });
                                    }
                                }}
                            />
                        </FormItem>
                        {type === 'COMPLAINTS_ASIC_2023' && (
                            <FormItem
                                label="Reporting period"
                                required
                                name="asicReportingPeriod"
                                rules={[{ required: true, message: 'Choose a reporting period' }]}
                            >
                                <Select
                                    options={generateLastTenPeriodsForAsicReport()
                                        .map((item) => ({ value: item }))
                                        .reverse()}
                                />
                            </FormItem>
                        )}
                        {(type === 'CLAIMS' ||
                            type === 'CLAIMS_BORDEREAU' ||
                            type === 'CLAIMS_GICOP_BREACH' ||
                            type === 'COMPLAINTS' ||
                            type === 'INCIDENTS' ||
                            type === 'IBCCC_BREACH_2025' ||
                            type === 'STAFF_TRAINING_SUMMARY' ||
                            type === 'STAFF_TRAINING_DETAILED') && (
                            <FormItem
                                label={
                                    type === 'CLAIMS' || type === 'CLAIMS_BORDEREAU' || type === 'CLAIMS_GICOP_BREACH'
                                        ? 'Lodgement date range'
                                        : 'Date range'
                                }
                                name="dateRange"
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            type === 'CLAIMS' ||
                                            type === 'CLAIMS_BORDEREAU' ||
                                            type === 'CLAIMS_GICOP_BREACH'
                                                ? 'Choose a lodgement date range'
                                                : 'Choose a date range',
                                    },
                                ]}
                            >
                                <RangePicker format={fDateShortDayJs} className="w-full" dateOnly />
                            </FormItem>
                        )}
                        {hasVisibleScope && (
                            <Form.Item
                                label={
                                    handledNoun
                                        ? `Report on ${handledNoun} handled by`
                                        : 'Handling parties to report on'
                                }
                                name="handlingPartyIds"
                                preserve={false}
                                rules={[{ required: true, message: 'Choose at least one handling party' }]}
                                extra={
                                    <>
                                        <p>
                                            Filters which {handledNoun ?? 'records'} appear in the report. Nothing is
                                            sent to the selected parties.
                                        </p>
                                        {visibleScopeUnavailable && (
                                            <Alert
                                                className="mt-2"
                                                role="status"
                                                showIcon
                                                icon={<CircleAlert size={20} aria-hidden="true" />}
                                                type="warning"
                                                title={
                                                    isUnassignedRegularUser
                                                        ? 'You are not assigned to any handling party'
                                                        : 'No handling parties are available for this report'
                                                }
                                                description={
                                                    isUnassignedRegularUser
                                                        ? 'Ask an organisation administrator to assign you to a handling party before generating this report.'
                                                        : 'Ask an organisation administrator to check your handling-party access before generating this report.'
                                                }
                                            />
                                        )}
                                    </>
                                }
                            >
                                <HandlingPartySelect
                                    key={type}
                                    onQueryStateChange={setVisiblePartiesQuery}
                                    showUnallocated={!isTrainingManagerOnly}
                                    forOrgAdminShowAll
                                    forTrainingManagerShowTmParties
                                    // A type change remounts this selector to refresh readiness,
                                    // but must retain a deliberately narrowed (or cleared) scope.
                                    autoSelectAll={handlingPartyIds === undefined}
                                    canSelectIsDisabledHandlingParty
                                    // "Covers records handled by …" reads wrong for staff training reports
                                    // (parties filter staff there), so no summary without a bound noun
                                    showSummary={!!handledNoun}
                                    summaryNoun={handledNoun}
                                />
                            </Form.Item>
                        )}
                        {isRegularUserTrainingReport && (
                            <>
                                {/* The value field stays hidden (regular users don't choose parties), but the
                                query status gets a visible owner below — a failed autoSelectAll otherwise
                                left the required input silently unpopulated (CUR-796). */}
                                <Form.Item
                                    name="handlingPartyIds"
                                    preserve={false}
                                    rules={[{ required: true, message: 'Choose at least one handling party' }]}
                                    hidden
                                >
                                    <HandlingPartySelect
                                        key={type}
                                        autoSelectAll
                                        onQueryStateChange={setHiddenPartiesQuery}
                                    />
                                </Form.Item>
                                {!hiddenPartiesQuery?.isReady && hiddenPartiesQuery?.error == null && (
                                    <Form.Item label="Report scope">
                                        <Space align="center" role="status" size="small">
                                            {/* curium:allow-spin — inline leaf-node loading indicator. */}
                                            <Spin size="small" />
                                            <span>Loading handling parties...</span>
                                        </Space>
                                    </Form.Item>
                                )}
                                {hiddenPartiesQuery?.error != null && !hiddenPartiesQuery.isReady && (
                                    <Form.Item label="Report scope">
                                        <InlineQueryError
                                            compact
                                            error={hiddenPartiesQuery.error}
                                            onRetry={hiddenPartiesQuery.retry}
                                            retrying={hiddenPartiesQuery.isRetrying && !hiddenPartiesQuery.canRetry}
                                            title="Handling parties failed to load"
                                        />
                                    </Form.Item>
                                )}
                                {hiddenPartiesQuery?.isReady && !handlingPartyIds?.length && (
                                    <Form.Item label="Report scope">
                                        <Alert
                                            description="Ask an organisation administrator to assign you to a handling party before generating this report."
                                            showIcon
                                            title="You are not assigned to any handling party"
                                            type="warning"
                                        />
                                    </Form.Item>
                                )}
                            </>
                        )}
                        {(type === 'CLAIMS' || type === 'CLAIMS_BORDEREAU' || type === 'CLAIMS_GICOP_BREACH') && (
                            <Row gutter={16}>
                                <Col span={12}>
                                    <FormItem label="Product" name="productTypeIds">
                                        <ProductTypeSelect mode="multiple" placeholder="All products" />
                                    </FormItem>
                                </Col>
                                <Col span={12}>
                                    <FormItem label="Insurer" name="insurerIds">
                                        <InsurerSelect mode="multiple" placeholder="All insurers" />
                                    </FormItem>
                                </Col>
                                <Col span={12}>
                                    <FormItem label="Broker" name="brokerCompanies">
                                        <Select
                                            placeholder="All brokers"
                                            loading={claimsReportFiltersQuery.isPending}
                                            mode="multiple"
                                            allowClear
                                            options={claimsReportFiltersQuery.data?.brokerCompanies.map((item) => ({
                                                value: item,
                                            }))}
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={12}>
                                    <FormItem label="Supplier name" name="supplierIds">
                                        <SupplierSelect mode="multiple" placeholder="All suppliers" />
                                    </FormItem>
                                </Col>
                                <Col span={12}>
                                    <FormItem label="Status" name="statuses">
                                        <Select
                                            placeholder="All statuses"
                                            loading={claimsReportFiltersQuery.isPending}
                                            mode="multiple"
                                            allowClear
                                            options={claimsReportFiltersQuery.data?.statuses.map((item) => ({
                                                value: item,
                                            }))}
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={12}>
                                    <FormItem label="Decision" name="decisions">
                                        <Select
                                            placeholder="All decisions"
                                            loading={claimsReportFiltersQuery.isPending}
                                            mode="multiple"
                                            allowClear
                                            options={claimsReportFiltersQuery.data?.decisions.map((item) => ({
                                                value: item,
                                            }))}
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={12}>
                                    <FormItem label="Triage outcome" name="triageOutcomes">
                                        <Select
                                            placeholder="All triage outcomes"
                                            loading={claimsReportFiltersQuery.isPending}
                                            mode="multiple"
                                            allowClear
                                            options={claimsReportFiltersQuery.data?.triageOutcomes.map((item) => ({
                                                value: item,
                                            }))}
                                        />
                                    </FormItem>
                                </Col>
                                <Col span={12}>
                                    <FormItem label="Cause of loss" name="causesOfLoss">
                                        <Select
                                            placeholder="All causes of loss"
                                            loading={claimsReportFiltersQuery.isPending}
                                            mode="multiple"
                                            allowClear
                                            options={claimsReportFiltersQuery.data?.causesOfLoss.map((item) => ({
                                                value: item,
                                            }))}
                                        />
                                    </FormItem>
                                </Col>
                            </Row>
                        )}
                        {(type === 'STAFF_TRAINING_SUMMARY' || type === 'STAFF_TRAINING_DETAILED') &&
                            (isRegularUserTrainingReport ? (
                                <Form.Item name="includeDisabledUsers" hidden initialValue={true}>
                                    <Switch />
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    label="Include disabled users"
                                    name="includeDisabledUsers"
                                    layout="horizontal"
                                    tooltip="Include users that have been deactivated but may still have training records associated with them"
                                    initialValue={true}
                                    colon={false}
                                >
                                    <Switch />
                                </Form.Item>
                            ))}
                    </Form>
                </>
            )}
        </OverlayPage>
    );
}
